// La bande d'onglets contextuelle : une seule règle, les onglets montrent le
// contenu du scope affiché en haut à gauche. Scope cabinet = Dossiers ·
// Procédures · Travaux · Fichiers. Scope dossier = Aperçu · Cahier · Revue ·
// TVA · Fichiers · Clôture. Une pastille rouge porte le compte de ce qui
// attend derrière un onglet (dossiers à traiter, travaux en attente…).
//
// Chaque portée a ses identifiants d'onglets, fermés : « cahier » côté
// cabinet, ou un onglet renommé sans sa route, ne compile pas.

import "./bande-onglets.css";

import type { ComptaIconeNom } from "@app/components/compta/ui/ComptaIcone";
import { ComptaIcone } from "@app/components/compta/ui/ComptaIcone";

export type ScopeOnglets = "cabinet" | "dossier";

export type OngletCabinet = "dossiers" | "procedures" | "travaux" | "fichiers";

export type OngletDossier =
  | "apercu"
  | "cahier"
  | "revue"
  | "tva"
  | "fichiers"
  | "cloture";

interface Onglet<Id extends string> {
  id: Id;
  libelle: string;
  icone: ComptaIconeNom;
}

const ONGLETS_CABINET: Onglet<OngletCabinet>[] = [
  { id: "dossiers", libelle: "Dossiers", icone: "users" },
  { id: "procedures", libelle: "Procédures", icone: "books" },
  { id: "travaux", libelle: "Travaux", icone: "suitcase" },
  { id: "fichiers", libelle: "Fichiers", icone: "folders" },
];

const ONGLETS_DOSSIER: Onglet<OngletDossier>[] = [
  { id: "apercu", libelle: "Aperçu", icone: "gauge" },
  { id: "cahier", libelle: "Cahier", icone: "book-open-text" },
  { id: "revue", libelle: "Revue", icone: "list-checks" },
  { id: "tva", libelle: "TVA", icone: "percent" },
  { id: "fichiers", libelle: "Fichiers", icone: "folders" },
  { id: "cloture", libelle: "Clôture", icone: "stamp" },
];

interface RangeeProps<Id extends string> {
  onglets: Onglet<Id>[];
  actif: Id;
  pastilles?: Partial<Record<Id, number>>;
  indisponibles?: Id[];
  onNaviguer?: (onglet: Id) => void;
}

// La rangée rendue, une fois la portée connue.
function Rangee<Id extends string>({
  onglets,
  actif,
  pastilles,
  indisponibles,
  onNaviguer,
}: RangeeProps<Id>) {
  return (
    <div className="cbande" role="tablist">
      {onglets.map((onglet) => {
        const estActif = onglet.id === actif;
        const pastille = pastilles?.[onglet.id];
        const bientot = indisponibles?.includes(onglet.id) ?? false;
        const classes = ["cbande__onglet"];
        if (estActif) {
          classes.push("cbande__onglet--actif");
        }
        if (bientot) {
          classes.push("cbande__onglet--bientot");
        }
        return (
          <button
            key={onglet.id}
            type="button"
            role="tab"
            aria-selected={estActif}
            aria-disabled={bientot ? true : undefined}
            title={bientot ? `${onglet.libelle} · bientôt` : undefined}
            className={classes.join(" ")}
            onClick={bientot ? undefined : () => onNaviguer?.(onglet.id)}
          >
            <ComptaIcone nom={onglet.icone} taille={18} />
            {onglet.libelle}
            {pastille !== undefined && pastille > 0 && (
              <span className="cbande__pastille">{pastille}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface BandeCabinetProps {
  scope: "cabinet";
  actif: OngletCabinet;
  // Le compte affiché en pastille rouge à droite du libellé, par onglet.
  pastilles?: Partial<Record<OngletCabinet, number>>;
  // Les onglets pas encore disponibles : grisés et inertes.
  indisponibles?: OngletCabinet[];
  onNaviguer?: (onglet: OngletCabinet) => void;
}

interface BandeDossierProps {
  scope: "dossier";
  actif: OngletDossier;
  pastilles?: Partial<Record<OngletDossier, number>>;
  indisponibles?: OngletDossier[];
  onNaviguer?: (onglet: OngletDossier) => void;
}

type BandeOngletsProps = BandeCabinetProps | BandeDossierProps;

export function BandeOnglets(props: BandeOngletsProps) {
  if (props.scope === "cabinet") {
    return (
      <Rangee
        onglets={ONGLETS_CABINET}
        actif={props.actif}
        pastilles={props.pastilles}
        indisponibles={props.indisponibles}
        onNaviguer={props.onNaviguer}
      />
    );
  }
  return (
    <Rangee
      onglets={ONGLETS_DOSSIER}
      actif={props.actif}
      pastilles={props.pastilles}
      indisponibles={props.indisponibles}
      onNaviguer={props.onNaviguer}
    />
  );
}
